import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  GlobalStyles,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { pygHeroShellSx } from "../PyGAnalitico/PygAnaliticoPageHeader";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

import { SidebarContext } from "../Sidebar/SidebarProvider";
import {
  fetchOrganigrama,
  saveOrganigramaRoles,
} from "../../api/Organigrama/organigramaApi";
import {
  DEFAULT_ROLES,
  ROLE_ORDER,
} from "./organigramaDefaultData";
import OrganigramaChart from "./OrganigramaChart";
import RoleCard from "./RoleCard";
import OrganigramaPrintHeader from "./OrganigramaPrintHeader";
import {
  clearOrganigramaPrintBodyClasses,
  organigramaPrintGlobalStyles,
  runOrganigramaPrint,
} from "./organigramaPrintStyles";
import { ORGANIGRAMA_ROLE_COLORS } from "./organigramaColors";
import {
  extractAllRolesContent,
  normalizeOrganigramaLocale,
  normalizeSavedLocales,
  resolveDisplayRoles,
} from "./organigramaRoleContent";

const FuncionesDirectivasGrid = ({ roles, className, editing = false, onRoleChange }) => (
  <Box
    className={className}
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr",
        md: "1fr 1fr",
        xl: "repeat(3, 1fr)",
      },
      gap: 2,
      alignItems: "stretch",
    }}
  >
    {ROLE_ORDER.map((key) => (
      <RoleCard
        key={key}
        role={roles[key] || DEFAULT_ROLES[key]}
        editing={editing}
        onChange={onRoleChange ? (next) => onRoleChange(key, next) : undefined}
      />
    ))}
  </Box>
);

const Organigrama = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { contentMargin } = useContext(SidebarContext);

  const [tab, setTab] = useState(0);
  const [savedLocales, setSavedLocales] = useState({});
  const [draftRoles, setDraftRoles] = useState(null);
  const [editing, setEditing] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canPrint, setCanPrint] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const onAfterPrint = () => {
      setPrinting(false);
      clearOrganigramaPrintBodyClasses();
    };
    window.addEventListener("afterprint", onAfterPrint);
    return () => {
      window.removeEventListener("afterprint", onAfterPrint);
      clearOrganigramaPrintBodyClasses();
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/login");
    try {
      const decoded = jwtDecode(token);
      if (decoded.exp < Date.now() / 1000) {
        localStorage.removeItem("token");
        navigate("/login");
      }
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { locales: savedLocalesPayload, canEdit: editable, canPrint: printable } =
        await fetchOrganigrama();
      setCanEdit(editable);
      setCanPrint(printable);
      setSavedLocales(normalizeSavedLocales({ locales: savedLocalesPayload }));
    } catch (err) {
      console.error(err);
      setSavedLocales({});
      setError(t("organigrama.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const currentLocale = normalizeOrganigramaLocale(i18n.language);

  const displayRoles = useMemo(() => {
    if (editing && draftRoles) return draftRoles;
    return resolveDisplayRoles(savedLocales, currentLocale, t);
  }, [savedLocales, draftRoles, editing, currentLocale, t]);

  useEffect(() => {
    if (!editing) return;
    setDraftRoles(null);
    setEditing(false);
    setSnack({
      open: true,
      message: t("organigrama.editCancelledLanguage"),
      severity: "info",
    });
  }, [currentLocale]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEditing = () => {
    setDraftRoles(
      JSON.parse(
        JSON.stringify(resolveDisplayRoles(savedLocales, currentLocale, t)),
      ),
    );
    setEditing(true);
    setTab(1);
  };

  const cancelEditing = () => {
    setDraftRoles(null);
    setEditing(false);
  };

  const handleSave = async () => {
    if (!draftRoles) return;
    setSaving(true);
    try {
      const locale = currentLocale;
      const content = extractAllRolesContent(draftRoles);
      await saveOrganigramaRoles(locale, content);
      setSavedLocales((prev) => ({
        ...prev,
        [locale]: content,
      }));
      setDraftRoles(null);
      setEditing(false);
      setSnack({
        open: true,
        message: t("organigrama.saveSuccessLocale", {
          language: t(`organigrama.localeNames.${locale}`),
        }),
        severity: "success",
      });
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        message: err?.response?.data?.error || t("organigrama.saveError"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const chartPanelSx = {
    p: { xs: 1.25, sm: 2 },
    borderRadius: 3,
    border: "1px solid",
    borderColor: alpha(theme.palette.divider, 0.85),
    bgcolor: theme.palette.mode === "dark" ? "background.paper" : "#f8fafb",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 28px rgba(0,0,0,0.22)"
        : "0 10px 36px rgba(15,23,42,0.05)",
  };

  const triggerPrint = useCallback(
    (mode) => {
      if (!canPrint) return;
      setPrinting(true);
      runOrganigramaPrint(mode);
    },
    [canPrint],
  );

  const handlePrintCurrent = useCallback(() => {
    triggerPrint(tab === 0 ? "chart" : "funciones");
  }, [tab, triggerPrint]);

  const accent = theme.palette.primary.main;

  return (
    <Box
      className="organigrama-print-root"
      sx={{ p: { xs: 1.25, sm: 2 }, ...contentMargin, pb: { xs: 10, sm: 4 } }}
    >
      <GlobalStyles styles={organigramaPrintGlobalStyles} />
      <Box
        className="organigrama-no-print"
        sx={pygHeroShellSx(theme, { sticky: !isMobile })}
      >
        <Box sx={{ p: { xs: 1.25, sm: 1.75 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: alpha(accent, 0.16),
                  color: theme.palette.primary.dark,
                  border: "1px solid",
                  borderColor: alpha(accent, 0.28),
                }}
              >
                <AccountTreeOutlinedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ fontWeight: 700, letterSpacing: 1.1 }}
                >
                  {t("organigrama.overline")}
                </Typography>
                <Typography
                  variant={isMobile ? "h5" : "h4"}
                  sx={{ fontWeight: 800, lineHeight: 1.1 }}
                >
                  {t("organigrama.title")}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  sx={{ mt: 0.75, flexWrap: "wrap" }}
                >
                  <Chip
                    size="small"
                    label={t("organigrama.chipCco")}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(ORGANIGRAMA_ROLE_COLORS.irene, 0.1),
                      color: ORGANIGRAMA_ROLE_COLORS.irene,
                    }}
                  />
                  <Chip
                    size="small"
                    label={t("organigrama.chipEc")}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(ORGANIGRAMA_ROLE_COLORS.miguel, 0.1),
                      color: ORGANIGRAMA_ROLE_COLORS.miguel,
                    }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("organigrama.chipCofo")}
                    sx={{
                      fontWeight: 700,
                      borderColor: alpha(ORGANIGRAMA_ROLE_COLORS.borja, 0.5),
                      color: ORGANIGRAMA_ROLE_COLORS.borja,
                    }}
                  />
                  <Chip
                    size="small"
                    label={t("organigrama.chipDesign")}
                    sx={{
                      fontWeight: 700,
                      bgcolor: alpha(ORGANIGRAMA_ROLE_COLORS.agustina, 0.1),
                      color: ORGANIGRAMA_ROLE_COLORS.agustina,
                    }}
                  />
                </Stack>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {canPrint ? (
                <>
                  <Button
                    variant="outlined"
                    fullWidth={isMobile}
                    startIcon={<PrintOutlinedIcon />}
                    onClick={handlePrintCurrent}
                    disabled={loading}
                  >
                    {tab === 0
                      ? t("organigrama.printChart")
                      : t("organigrama.printRoles")}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth={isMobile}
                    startIcon={<PrintOutlinedIcon />}
                    onClick={() => triggerPrint("all")}
                    disabled={loading}
                  >
                    {t("organigrama.printAll")}
                  </Button>
                </>
              ) : null}
              {canEdit ? (
                editing ? (
                  <>
                    <Button
                      variant="outlined"
                      fullWidth={isMobile}
                      startIcon={<CloseOutlinedIcon />}
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      {t("organigrama.cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth={isMobile}
                      startIcon={
                        saving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SaveOutlinedIcon />
                        )
                      }
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {t("organigrama.save")}
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    fullWidth={isMobile}
                    startIcon={<EditOutlinedIcon />}
                    onClick={startEditing}
                  >
                    {t("organigrama.editRoles")}
                  </Button>
                )
              ) : null}
            </Stack>
          </Stack>
          {/*    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
            Miguel e Irene en dirección ejecutiva; ventas y diseño a la izquierda, operaciones de Borja a la derecha
          </Typography> */}
        </Box>
      </Box>

      {editing ? (
        <Alert severity="info" sx={{ mb: 2 }} className="organigrama-no-print">
          {t("organigrama.editLocaleHint", {
            language: t(`organigrama.localeNames.${currentLocale}`),
          })}
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }} className="organigrama-no-print">
          {error}
        </Alert>
      ) : null}

      <Tabs
        className="organigrama-no-print"
        value={tab}
        onChange={(_, value) => setTab(value)}
        variant={isMobile ? "fullWidth" : "standard"}
        sx={{
          mt: 1.5,
          mb: 2,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": {
            fontWeight: 700,
            minHeight: 48,
            fontSize: { xs: "0.8rem", sm: "0.875rem" },
          },
        }}
      >
        <Tab
          icon={<AccountTreeOutlinedIcon />}
          iconPosition="start"
          label={t("organigrama.tabChart")}
        />
        <Tab
          icon={<BadgeOutlinedIcon />}
          iconPosition="start"
          label={t("organigrama.tabRoles")}
        />
      </Tabs>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading ? (
        <>
          <Box
            className="organigrama-section-chart"
            sx={{ display: tab === 0 ? "block" : "none" }}
          >
            <Box className="organigrama-print-header">
              <OrganigramaPrintHeader
                title={t("organigrama.tabChart")}
                subtitle={t("organigrama.printChartSubtitle")}
              />
            </Box>
            <Box className="organigrama-chart-root" sx={chartPanelSx}>
              <OrganigramaChart />
            </Box>
          </Box>

          <Box
            className="organigrama-section-funciones"
            sx={{ display: tab === 1 ? "block" : "none" }}
          >
            <Box className="organigrama-print-header">
              <OrganigramaPrintHeader
                title={t("organigrama.tabRoles")}
                subtitle={t("organigrama.printRolesSubtitle")}
              />
            </Box>
            <FuncionesDirectivasGrid
              roles={displayRoles}
              editing={editing && !printing}
              className="organigrama-funciones-print"
              onRoleChange={(key, next) =>
                setDraftRoles((prev) => ({
                  ...(prev || displayRoles),
                  [key]: next,
                }))
              }
            />
          </Box>
        </>
      ) : null}

      <Snackbar
        className="organigrama-no-print"
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Organigrama;
